import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/pay";
import { KEY_MOMENTS } from "@/pages/Landing";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Inbox, Building2, AlertCircle } from "lucide-react";
import { OfferCard } from "@/components/tenant/OfferCard";
import { MatchedPropertyCard } from "@/components/tenant/MatchedPropertyCard";
import { ReferralCard } from "@/components/tenant/ReferralCard";
import { ScreeningReport } from "@/components/ScreeningReport";
import { MatchmakingStatusBar } from "@/components/tenant/MatchmakingStatusBar";
import { DistanceControl } from "@/components/tenant/DistanceControl";
import { DeleteAccountZone } from "@/components/DeleteAccountZone";
import { PageBackground } from "@/components/PageBackground";

export default function TenantDashboard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [offers, setOffers] = useState([]);
  const [matched, setMatched] = useState([]);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [signOffer, setSignOffer] = useState(null);
  const [distAddr, setDistAddr] = useState("");
  const [distRadius, setDistRadius] = useState(25);

  const load = useCallback(async () => {
    const [pr, off] = await Promise.all([api.get("/tenant/profile"), api.get("/offers/inbox")]);
    setProfile(pr.data);
    setDistAddr(pr.data.home_address || "");
    setDistRadius(pr.data.search_radius_miles || 25);
    setOffers(off.data);
    off.data.filter((o) => o.status === "sent").forEach((o) => api.post(`/offers/${o.id}/view`));
    if (pr.data.verified && pr.data.matchmaking_status === "active") {
      const m = await api.get("/tenant/matched-properties");
      setMatched(m.data);
    } else setMatched([]);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDistance = async () => {
    try {
      await api.put("/tenant/profile", { home_address: distAddr, search_radius_miles: Number(distRadius) });
      toast.success("Distance range updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const verify = () => startCheckout("verification");
  const verifyFree = async () => {
    try {
      await api.post("/tenant/verify/free");
      toast.success("You're verified — welcome to the pool! 🎉");
      await refresh();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't verify"); }
  };
  const pause = async () => { await api.post("/tenant/matchmaking/pause"); toast.success("Matchmaking paused"); load(); };
  const reactivate = () => startCheckout("reactivation");
  const resume = async () => {
    try {
      await api.post("/tenant/matchmaking/resume");
      toast.success("Matchmaking resumed — you're live again!");
      await refresh();
      load();
    } catch (e) {
      if (e.response?.status === 402) { setReactivateOpen(true); }
      else toast.error(e.response?.data?.detail || "Could not resume");
    }
  };

  const respond = async (id, action) => {
    try {
      await api.post(`/offers/${id}/respond?action=${action}`);
      toast.success(`Offer ${action === "accept" ? "accepted" : "declined"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sign = async () => {
    await api.post(`/offers/${signOffer.id}/sign`);
    toast.success("Lease signed — deal closed!");
    setSignOffer(null);
    load();
  };

  const interest = async (pid) => { await api.post(`/tenant/properties/${pid}/interest`); toast.success("Interest sent to landlord"); load(); };

  if (!profile) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  const active = profile.matchmaking_status === "active";

  return (
    <div>
      <PageBackground variant={2} />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 space-y-8" data-testid="tenant-dashboard">
        <MatchmakingStatusBar
          profile={profile} user={user} active={active}
          onVerify={verify} onVerifyFree={verifyFree}
          onPause={pause} onResume={resume} onReactivate={() => setReactivateOpen(true)}
        />

        {/* Profile completeness */}
        {!profile.profile_complete && (
          <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm"><AlertCircle className="w-4 h-4 text-primary" /> Complete your rental profile to attract better offers.</div>
            <Button size="sm" variant="outline" data-testid="complete-profile-btn" onClick={() => navigate("/tenant/profile")}>Edit profile</Button>
          </div>
        )}

        {/* Screening report */}
        {profile.verified && <ScreeningReport profile={profile} />}

        <DistanceControl
          distAddr={distAddr} setDistAddr={setDistAddr}
          distRadius={distRadius} setDistRadius={setDistRadius}
          onApply={saveDistance}
        />

        {/* Invite a friend */}
        {profile.verified && <ReferralCard />}

        {/* Offer inbox */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Inbox className="w-5 h-5 text-primary" />
            <h2 className="font-display text-2xl font-semibold tracking-tight">Offer inbox</h2>
            <span className="text-sm text-muted-foreground">({offers.length})</span>
          </div>
          {offers.length === 0 ? (
            <div className="bg-white border border-border rounded-sm p-12 text-center">
              <img src={KEY_MOMENTS[2].url} alt="happy renter" className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-sm" />
              <p className="text-muted-foreground">No offers yet — but your keys are coming. Stay active and verified to get pitched by landlords.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.map((o, i) => (
                <OfferCard key={o.id} offer={o} index={i} onRespond={respond} onSign={setSignOffer} onMessage={() => navigate("/messages")} />
              ))}
            </div>
          )}
        </div>

        {/* Matched properties */}
        {matched.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-primary" />
              <h2 className="font-display text-2xl font-semibold tracking-tight">Properties matching you</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {matched.map((m) => (<MatchedPropertyCard key={m.id} prop={m} onInterest={interest} />))}
            </div>
          </div>
        )}

        <DeleteAccountZone />
      </div>

      {/* Reactivate dialog */}
      <Dialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reactivate matchmaking</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A <b>$10 reactivation fee</b> runs a fresh credit check and refreshes your verified badge before your profile goes live again.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-reactivate" onClick={reactivate}>Pay $10 & reactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign lease dialog */}
      <Dialog open={!!signOffer} onOpenChange={(o) => !o && setSignOffer(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Lease for {signOffer?.title}</DialogTitle></DialogHeader>
          <Textarea readOnly value={signOffer?.lease_content || ""} rows={10} className="text-sm" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOffer(null)}>Close</Button>
            <Button data-testid="confirm-sign" onClick={sign}>Sign lease</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
